import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { DefaultLayer } from '@craftjs/layers';
import { TEMPLATES_PATHS } from '@common/network/ApiPaths';
import Notification, {
  SEVERITY_OPTIONS,
  useNotification,
} from '@common/components/Notification/Notification';

import Images from '@resolvers/common/Images/Images';

import { Box, Tabs, Tab, Typography } from '@mui/material';

import Toolbox from '../Toolbox/Toolbox';
import ConfigPanel from './ConfigPanel';
import BottomFormButtons from './BottomFormButtons';
import Recipients from './Recipients';

import styles from './TabPanel.module.scss';
import TextWithPlaceholderInput from '@resolvers/tools/TextWithPlaceholderInput/TextWithPlaceholderInput';
import FilteredLayers from '@common/components/ErrorBounderies/ErrorBounderies';
import { getByPathAndParams } from '@services/newServices';
import { selectBackgroundColor } from '@common/providers/ThemeProvider/selectors';
import { useTemplateDetailsStore } from '@store/templateDetailsStore';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const TAB_PANEL_OPTION_VALUES = Object.freeze({
  SUBJECT: 0,
  COMPONENTS: 1,
  CONFIG: 2,
  IMAGES: 3,
  RECIPIENTS: 4,
});

const TAB_PANEL_OPTIONS = Object.freeze([
  {
    label: 'subject_tab',
    value: TAB_PANEL_OPTION_VALUES.SUBJECT,
  },
  {
    label: 'tab_components',
    value: TAB_PANEL_OPTION_VALUES.COMPONENTS,
  },
  {
    label: 'tab_config',
    value: TAB_PANEL_OPTION_VALUES.CONFIG,
  },
  {
    label: 'image_tab',
    value: TAB_PANEL_OPTION_VALUES.IMAGES,
  },
  {
    label: 'recipients_button',
    value: TAB_PANEL_OPTION_VALUES.RECIPIENTS,
    hideInStandalone: true,
  },
]);

const TabPanel = ({
  templateType,
  frameEditorRef,
  ready,
  setSaveLoading,
  templateSubject,
  isCustomEmail,
  isCreateMode,
  currentLanguage,
}) => {
  const source = useTemplateDetailsStore((s) => s.source);
  const setCurrentEditorSubject = useTemplateDetailsStore(
    (s) => s.setCurrentEditorSubject,
  );

  const [selectedPanel, setSelectedPanel] = useState(0);
  const [demoData] = useState(source?.structure || {});
  const [recipients, setRecipients] = useState([]);
  const [imgs, setImgs] = useState([]);

  const { t } = useTranslation();
  const [showStatuses, showNotification, handleClose] = useNotification();

  const handleChange = (event, newValue) => {
    setSelectedPanel(newValue);
  };

  const setCurrentSubject = (newSubject) => {
    setCurrentEditorSubject({
      newSubject,
      currentLanguage,
    });
  };

  const fetchTemplateImages = async () => {
    try {
      let response = await getByPathAndParams({
        path: TEMPLATES_PATHS.GET_MEDIA,
      });
      if (response.status === 200) {
        setImgs(response.data);
      }
    } catch (err) {
      showNotification(SEVERITY_OPTIONS.ERROR, t('get_images_error'));
    }
  };

  useEffect(() => {
    fetchTemplateImages();
  }, []);

  const tabStyle = {
    p: '0px 5px',
    minHeight: '38px',
    fontSize: '12px',
    lineHeight: '1',
  };

  const settingHeight = isCustomEmail ? '50px' : '135px';

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          zIndex: 10,
          height: `calc(100vh - ${settingHeight})`,
        }}
      >
        <Tabs
          variant='scrollable'
          scrollButtons='auto'
          value={selectedPanel}
          onChange={handleChange}
          aria-label='basic tabs example'
          sx={{
            minHeight: '40px',
            mb: '2px',
            mt: '4px',
            backgroundColor: selectBackgroundColor,
          }}
        >
          {TAB_PANEL_OPTIONS.map(
            (option) =>
              (isCustomEmail || !option.hideInStandalone) && (
                <Tab
                  key={option.label}
                  sx={tabStyle}
                  label={t(option.label)}
                  {...a11yProps(option.value)}
                />
              ),
          )}
        </Tabs>
        <Box
          sx={{
            height: isCreateMode
              ? { xs: '12rem' }
              : `calc(100vh - ${settingHeight} - 250px - 50px)`,
            overflowY: 'auto',
          }}
        >
          <CustomTabPanel
            value={selectedPanel}
            index={TAB_PANEL_OPTION_VALUES.SUBJECT}
          >
            <TextWithPlaceholderInput
              value={templateSubject}
              setValue={setCurrentSubject}
              label={t('email_subject')}
              placeholder={t('subject_placeholder')}
              helperText={t('subject_helper_text')}
              sx={{
                p: 1,
                backgroundColor: selectBackgroundColor,
              }}
            />
          </CustomTabPanel>
          <CustomTabPanel
            value={selectedPanel}
            index={TAB_PANEL_OPTION_VALUES.COMPONENTS}
          >
            <Toolbox templateType={templateType} />
          </CustomTabPanel>
          <CustomTabPanel
            value={selectedPanel}
            index={TAB_PANEL_OPTION_VALUES.CONFIG}
          >
            <ConfigPanel />
          </CustomTabPanel>
          <CustomTabPanel
            value={selectedPanel}
            index={TAB_PANEL_OPTION_VALUES.IMAGES}
          >
            <Images
              imgs={imgs}
              setImgs={setImgs}
              fetchTemplateImages={fetchTemplateImages}
            />
          </CustomTabPanel>

          {isCustomEmail && (
            <CustomTabPanel
              value={selectedPanel}
              index={TAB_PANEL_OPTION_VALUES.RECIPIENTS}
            >
              <Recipients
                recipients={recipients}
                setRecipients={setRecipients}
              />
            </CustomTabPanel>
          )}
        </Box>
        <Typography variant='subtitle1' sx={{ pl: 1 }}>
          {t('page_layout')}
        </Typography>
        <Box
          sx={{
            backgroundColor: (theme) => theme.palette.primary.contrastText,
            height: isCreateMode
              ? { xs: '7.5rem', xl: '15rem' }
              : `calc(100vh - ${settingHeight} - calc(100vh - ${settingHeight} - 250px - 50px) - 70px)`,
            overflowY: 'auto',
          }}
        >
          {ready && (
            <FilteredLayers
              expandRootOnLoad={false}
              renderLayer={CustomLayer}
            />
          )}
        </Box>
        <Notification showStatuses={showStatuses} handleClose={handleClose} />
      </Box>
      {!isCreateMode && (
        <BottomFormButtons
          frameEditorRef={frameEditorRef}
          demoData={demoData}
          setSaveLoading={setSaveLoading}
          templateSubject={templateSubject}
          isCustomEmail={isCustomEmail}
          recipients={recipients}
          setRecipients={setRecipients}
          currentLanguage={currentLanguage}
        />
      )}
    </Box>
  );
};

const CustomLayer = ({ id, ...props }) => {
  return (
    <div className={styles.layersCustom}>
      <DefaultLayer id={id} {...props}>
        {props.children}
      </DefaultLayer>
    </div>
  );
};

TabPanel.propTypes = {
  templateType: PropTypes.string,
};

export default TabPanel;
